import { useState, useEffect, useCallback, useRef } from 'react'
import { useSubscription } from '@/libs/supabase/hooks/useSubscription'
import {
  ICustomerSalesData,
  IPaymentSummaryData,
  IExpensesByType,
  IDailySalesExportData,
  IUpdateCustomerPaymentInput,
  ILocalPaymentEntry,
} from '@/supabase/DailySalesSummary/dailySalesSummary.interface'
import {
  getDailySalesData,
  updateRemainingCash,
  updateCustomerPayments,
  calculatePaymentSummary
} from '@/supabase/DailySalesSummary/dailySalesSummary'
import { toggleVoidOrder } from '@/supabase/Orderbook/orderbook'
import { usePaymentEntries } from '@/hooks/usePaymentEntries'

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

interface UseDailySalesDataReturn {
  // Data
  customerSalesData: ICustomerSalesData[]
  paymentSummary: IPaymentSummaryData
  expenses: IExpensesByType
  dailySalesData: IDailySalesExportData | null
  
  // Loading states
  loading: boolean
  updating: boolean
  
  // Error state
  error: string | null
  
  // Actions
  refreshData: () => Promise<void>
  updateRemainingCashAmount: (amount: number) => Promise<boolean>
  updateAllCustomerPayments: (updates: IUpdateCustomerPaymentInput[]) => Promise<boolean>
  
  // Local state management for edits (now using orderId as key)
  localPaymentUpdates: Map<string, IUpdateCustomerPaymentInput>
  updateLocalPayment: (rowKey: string, update: Partial<IUpdateCustomerPaymentInput>) => void
  clearLocalUpdates: () => void
  hasLocalChanges: boolean

  // Payment entry management (from usePaymentEntries, keyed by rowKey)
  addPaymentEntry: (rowKey: string) => string
  updatePaymentEntry: (rowKey: string, tempId: string, updates: Partial<ILocalPaymentEntry>) => void
  deletePaymentEntry: (rowKey: string, tempId: string) => void
  savePaymentChanges: (date: string) => Promise<boolean>
  localPaymentEntries: Map<string, ILocalPaymentEntry[]>
  savingPayments: boolean
  hasPaymentChanges: boolean

  // Auto-save
  autoSaveStatus: AutoSaveStatus

  // Void order management
  handleToggleVoid: (orderId: number, isVoided: boolean) => Promise<boolean>
}

export const useDailySalesData = (selectedDate: string): UseDailySalesDataReturn => {
  // TODO: this
  // Subscribe to OrderBook changes for real-time updates
  const { rows: orderBookRows } = useSubscription('OrderBook')
  
  // Payment entries hook
  const paymentHook = usePaymentEntries()
  const { initializeFromServer, registerRowKey, clearAllEntries } = paymentHook

  // Data states
  const [customerSalesData, setCustomerSalesData] = useState<ICustomerSalesData[]>([])
  const [paymentSummary, setPaymentSummary] = useState<IPaymentSummaryData>({
    totalCashPayments: 0,
    totalTransferPayments: 0,
    totalOutstandingBalance: 0,
    totalPayments: 0,
    remainingCashTHB: 0
  })
  const [expenses, setExpenses] = useState<IExpensesByType>({
    FUEL: [],
    EMPLOYEE_OVERTIME: [],
    OTHER: []
  })
  const [dailySalesData, setDailySalesData] = useState<IDailySalesExportData | null>(null)
  
  // Refs to hold latest values for use inside useEffect without causing re-runs
  const paymentSummaryRef = useRef<IPaymentSummaryData>({
    totalCashPayments: 0,
    totalTransferPayments: 0,
    totalOutstandingBalance: 0,
    totalPayments: 0,
    remainingCashTHB: 0
  })
  const dailySalesDataRef = useRef<IDailySalesExportData | null>(null)
  
  // Loading and error states
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle')
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoSaveSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Ref to hold the latest selectedDate for use inside the debounced auto-save callback
  const selectedDateRef = useRef(selectedDate)
  selectedDateRef.current = selectedDate

  // Local state for payment edits (using rowKey: customerId-orderId)
  const [localPaymentUpdates, setLocalPaymentUpdates] = useState<Map<string, IUpdateCustomerPaymentInput>>(new Map())

  // Load data for selected date
  const loadData = useCallback(async () => {
    if (!selectedDate) return

    console.log(`📥 Loading data for date: ${selectedDate}`)
    setLoading(true)
    setError(null)

    try {
      const data = await getDailySalesData(selectedDate)
      
      if (data) {
        console.log(`📊 Loaded customer data:`, JSON.stringify(data.customers.map(c => ({
          customerId: c.customerId,
          paymentStatus: c.paymentStatus,
          amountPaidTHB: c.amountPaidTHB,
          outstandingBalanceTHB: c.outstandingBalanceTHB
        })), null, 2))
        
        setCustomerSalesData(data.customers)
        setPaymentSummary(data.paymentSummary)
        paymentSummaryRef.current = data.paymentSummary
        setExpenses(data.expenses)
        setDailySalesData(data)
        dailySalesDataRef.current = data

        // Clear old local payment entries before re-initializing from server
        clearAllEntries()

        // Initialize payment entries from server for each row using unique rowKey
        for (const customer of data.customers) {
          const rowKey = customer.rowType === 'ORDER' && customer.orderId != null
            ? `${customer.customerId}-${customer.orderId}`
            : `${customer.customerId}-payment`

          // Always register the rowKey → customerId + orderBookId mapping so save works correctly
          registerRowKey(rowKey, customer.customerId, customer.orderId)

          if (customer.paymentEntries && customer.paymentEntries.length > 0) {
            initializeFromServer(rowKey, customer.customerId, customer.orderId, customer.paymentEntries)
          }
        }
      } else {
        // Initialize empty data if no data found
        setCustomerSalesData([])
        const emptyPaymentSummary = {
          totalCashPayments: 0,
          totalTransferPayments: 0,
          totalOutstandingBalance: 0,
          totalPayments: 0,
          remainingCashTHB: 0
        }
        paymentSummaryRef.current = emptyPaymentSummary
        setPaymentSummary(emptyPaymentSummary)
        setExpenses({
          FUEL: [],
          EMPLOYEE_OVERTIME: [],
          OTHER: []
        })
        dailySalesDataRef.current = null
        setDailySalesData(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการโหลดข้อมูล')
      console.error('Error loading daily sales data:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedDate, initializeFromServer, registerRowKey, clearAllEntries])

  // Refresh data
  const refreshData = useCallback(async () => {
    await loadData()
    setLocalPaymentUpdates(new Map()) // Clear local updates after refresh
  }, [loadData])

  // Update remaining cash
  const updateRemainingCashAmount = useCallback(async (amount: number): Promise<boolean> => {
    if (!selectedDate) return false

    setUpdating(true)
    setError(null)

    try {
      const result = await updateRemainingCash(selectedDate, amount)
      
      if (result) {
        // Update local state
        setPaymentSummary(prev => {
          const updated = { ...prev, remainingCashTHB: amount }
          paymentSummaryRef.current = updated
          return updated
        })
        
        // Update daily sales data
        setDailySalesData(prev => {
          if (!prev) return null
          const updated = {
            ...prev,
            paymentSummary: { ...prev.paymentSummary, remainingCashTHB: amount }
          }
          dailySalesDataRef.current = updated
          return updated
        })
        
        return true
      } else {
        setError('ไม่สามารถอัปเดตเงินสดคงเหลือได้')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการอัปเดตเงินสดคงเหลือ')
      console.error('Error updating remaining cash:', err)
      return false
    } finally {
      setUpdating(false)
    }
  }, [selectedDate])

  // Update all customer payments
  const updateAllCustomerPayments = useCallback(async (updates: IUpdateCustomerPaymentInput[]): Promise<boolean> => {
    if (!selectedDate || updates.length === 0) {
      return false
    }

    console.log(`📝 Setting updating state...`)
    setUpdating(true)
    setError(null)

    try {
      // Don't aggregate - update each order separately
      console.log(`🔄 Calling updateCustomerPayments API with individual updates...`)
      console.log(`Updates to process:`, JSON.stringify(updates, null, 2))
      
      const result = await updateCustomerPayments(selectedDate, updates)
      console.log(`✅ API result:`, result)
      
      if (result) {
        console.log(`🎯 Updating local customer data...`)
        console.log(`Updates to apply:`, JSON.stringify(updates, null, 2))
        
        // Apply local updates to current data immediately for better UX
        setCustomerSalesData(prev => {
          console.log(`Previous customerSalesData:`, JSON.stringify(prev.map(c => ({
            customerId: c.customerId,
            orderId: c.orderId,
            paymentStatus: c.paymentStatus,
            amountPaidTHB: c.amountPaidTHB,
            outstandingBalanceTHB: c.outstandingBalanceTHB
          })), null, 2))
          
          const newData = prev.map(customer => {
            // Find update for this specific row (using rowKey logic)
            const rowKey = customer.rowType === 'ORDER' ? `${customer.customerId}-${customer.orderId}` : `${customer.customerId}-payment`
            const localUpdate = localPaymentUpdates.get(rowKey)
            
            if (localUpdate) {
              console.log(`Updating customer ${customer.customerId} order ${customer.orderId}:`, {
                old: {
                  amountPaidTHB: customer.amountPaidTHB,
                  outstandingBalanceTHB: customer.outstandingBalanceTHB
                },
                new: {
                  amountPaidTHB: localUpdate.amountPaidTHB,
                  outstandingBalanceTHB: localUpdate.outstandingBalanceTHB
                }
              })
              return {
                ...customer,
                amountPaidTHB: localUpdate.amountPaidTHB,
                outstandingBalanceTHB: localUpdate.outstandingBalanceTHB
              }
            }
            return customer
          })
          
          console.log(`New customerSalesData:`, JSON.stringify(newData.map(c => ({
            customerId: c.customerId,
            orderId: c.orderId,
            paymentStatus: c.paymentStatus,
            amountPaidTHB: c.amountPaidTHB,
            outstandingBalanceTHB: c.outstandingBalanceTHB
          })), null, 2))
          
          return newData
        })
        
        console.log(`🧹 Clearing local updates...`)
        // Clear local updates immediately
        setLocalPaymentUpdates(new Map())
        
        
        // Refresh data in background to ensure consistency (with small delay)
        setTimeout(async () => {
          
          await loadData()
          
        }, 100)
        
        
        return true
      } else {
        
        setError('ไม่สามารถอัปเดตข้อมูลการชำระเงินได้')
        return false
      }
    } catch (err) {
      
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการอัปเดตข้อมูลการชำระเงิน')
      console.error('Error updating customer payments:', err)
      return false
    } finally {
      setUpdating(false)
    }
  }, [selectedDate, loadData, localPaymentUpdates])

  // Update local payment data (for editing before save) - now using rowKey
  const updateLocalPayment = useCallback((rowKey: string, update: Partial<IUpdateCustomerPaymentInput>) => {
    setLocalPaymentUpdates(prev => {
      const newMap = new Map(prev)
      
      // Extract customerId from rowKey (format: customerId-orderId or customerId-payment)
      const customerId = rowKey.split('-')[0]
      
      // Get existing local update or use current row data as base
      const existingLocalUpdate = newMap.get(rowKey)
      const currentRow = customerSalesData.find(c => 
        c.rowType === 'ORDER' ? `${c.customerId}-${c.orderId}` === rowKey : `${c.customerId}-payment` === rowKey
      )
      
      const existing = existingLocalUpdate || {
        customerId,
        amountPaidTHB: currentRow?.amountPaidTHB || 0,
        outstandingBalanceTHB: currentRow?.outstandingBalanceTHB || 0,
        orderId: currentRow?.orderId // Include orderId for order-specific updates
      }
      
      newMap.set(rowKey, {
        ...existing,
        ...update,
        customerId
      })
      
      return newMap
    })
  }, [customerSalesData])

  // Clear local updates
  const clearLocalUpdates = useCallback(() => {
    setLocalPaymentUpdates(new Map())
  }, [])

  // Check if there are local changes (includes both old payment updates and new payment entries)
  const hasLocalChanges = localPaymentUpdates.size > 0 || paymentHook.hasChanges

  // Handle void order toggle
  const handleToggleVoid = useCallback(async (orderId: number, isVoided: boolean): Promise<boolean> => {
    const success = await toggleVoidOrder(orderId, isVoided)
    if (success) {
      await refreshData()
    }
    return success
  }, [refreshData])

  // Load data when selectedDate changes
  useEffect(() => {
    
    loadData()
  }, [loadData, selectedDate])

  // Auto-refresh when OrderBook changes (real-time updates)
  useEffect(() => {
    if (orderBookRows && selectedDate) {
      // Debounce the refresh to avoid too many calls
      const timeoutId = setTimeout(() => {
        
        loadData()
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [orderBookRows, selectedDate, loadData])

  // Recalculate payment summary when customer data, local updates, or local payment entries change
  useEffect(() => {
    if (customerSalesData.length > 0) {
      // Apply local payment entry changes to customer data for real-time calculation
      const updatedCustomerData = customerSalesData.map(customer => {
        // First apply old-style local payment updates (key is rowKey, not customerId)
        const rowKeyForUpdate = customer.rowType === 'ORDER' && customer.orderId != null
          ? `${customer.customerId}-${customer.orderId}`
          : `${customer.customerId}-payment`
        const localUpdate = localPaymentUpdates.get(rowKeyForUpdate)
        let updatedCustomer = customer
        if (localUpdate) {
          updatedCustomer = {
            ...updatedCustomer,
            amountPaidTHB: localUpdate.amountPaidTHB,
            outstandingBalanceTHB: localUpdate.outstandingBalanceTHB
          }
        }

        // Then merge local payment entries for real-time summary calculation
        const rowKey = customer.rowType === 'ORDER' && customer.orderId != null
          ? `${customer.customerId}-${customer.orderId}`
          : `${customer.customerId}-payment`
        const localEntries = paymentHook.localPaymentEntries.get(rowKey)
        if (localEntries && localEntries.length > 0) {
          // Build effective payment entries: filter out deleted, include new and modified
          const effectiveEntries = localEntries
            .filter(e => e.status !== 'deleted')
            .map(e => ({
              id: e.id || e.tempId,
              dailyCustomerSummaryId: e.dailyCustomerSummaryId || '',
              amountTHB: e.amountTHB,
              paymentMethod: e.paymentMethod,
              note: e.note,
              createdAt: '',
              updatedAt: '',
            }))

          // Recalculate amountPaidTHB from local entries
          const localAmountPaid = effectiveEntries.reduce((sum, e) => sum + e.amountTHB, 0)
          
          updatedCustomer = {
            ...updatedCustomer,
            paymentEntries: effectiveEntries,
            amountPaidTHB: localAmountPaid,
            outstandingBalanceTHB: Math.max(0, updatedCustomer.totalAmountTHB - localAmountPaid),
          }
        }

        return updatedCustomer
      })

      // Use refs to read latest values without adding them to deps (avoids infinite loop)
      const currentRemainingCash = paymentSummaryRef.current.remainingCashTHB
      const newPaymentSummary = calculatePaymentSummary(updatedCustomerData, currentRemainingCash)
      
      // Only update if there's actually a change to prevent infinite loop
      const prev = paymentSummaryRef.current
      const hasPaymentSummaryChanged = 
        newPaymentSummary.totalCashPayments !== prev.totalCashPayments ||
        newPaymentSummary.totalTransferPayments !== prev.totalTransferPayments ||
        newPaymentSummary.totalOutstandingBalance !== prev.totalOutstandingBalance
      
      if (hasPaymentSummaryChanged) {
        paymentSummaryRef.current = newPaymentSummary
        setPaymentSummary(newPaymentSummary)
        
        // Update dailySalesData with new payment summary
        if (dailySalesDataRef.current) {
          const updated = {
            ...dailySalesDataRef.current,
            customers: updatedCustomerData,
            paymentSummary: newPaymentSummary
          }
          dailySalesDataRef.current = updated
          setDailySalesData(updated)
        }
      }
    }
  }, [customerSalesData, localPaymentUpdates, paymentHook.localPaymentEntries])

  // --- Auto-save with 2-second debounce (Google Sheets style) ---
  // Watches for payment entry changes and automatically saves after 2s of inactivity.
  const autoSave = useCallback(async () => {
    const date = selectedDateRef.current
    const hasNewPaymentEntryChanges = paymentHook.hasChanges
    const hasOldStyleUpdates = localPaymentUpdates.size > 0

    if (!hasNewPaymentEntryChanges && !hasOldStyleUpdates) {
      setAutoSaveStatus('idle')
      return
    }

    setAutoSaveStatus('saving')

    let allSuccess = true

    // Save new payment entry changes
    if (hasNewPaymentEntryChanges) {
      const paymentSuccess = await paymentHook.saveAllChanges(date)
      if (!paymentSuccess) allSuccess = false
    }

    // Save old-style payment updates
    if (hasOldStyleUpdates) {
      const updates = Array.from(localPaymentUpdates.values())
      const oldSuccess = await updateAllCustomerPayments(updates)
      if (!oldSuccess) allSuccess = false
    }

    if (allSuccess) {
      setAutoSaveStatus('saved')
      // Refresh data from server to sync
      await loadData()
      // Reset "saved" indicator after 2 seconds
      autoSaveSavedTimerRef.current = setTimeout(() => {
        setAutoSaveStatus('idle')
      }, 2000)
    } else {
      setAutoSaveStatus('error')
      // Reset error indicator after 3 seconds
      autoSaveSavedTimerRef.current = setTimeout(() => {
        setAutoSaveStatus('idle')
      }, 3000)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentHook.hasChanges, paymentHook.saveAllChanges, localPaymentUpdates, updateAllCustomerPayments, loadData])

  // Trigger debounced auto-save whenever payment entries change
  useEffect(() => {
    const hasChangesNow = paymentHook.hasChanges || localPaymentUpdates.size > 0

    // Clear any existing timers
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
      autoSaveTimerRef.current = null
    }
    if (autoSaveSavedTimerRef.current) {
      clearTimeout(autoSaveSavedTimerRef.current)
      autoSaveSavedTimerRef.current = null
    }

    if (hasChangesNow) {
      setAutoSaveStatus('pending')
      autoSaveTimerRef.current = setTimeout(() => {
        autoSave()
      }, 2000)
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentHook.localPaymentEntries, localPaymentUpdates])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      if (autoSaveSavedTimerRef.current) clearTimeout(autoSaveSavedTimerRef.current)
    }
  }, [])

  return {
    // Data
    customerSalesData,
    paymentSummary,
    expenses,
    dailySalesData,
    
    // Loading states
    loading,
    updating,
    
    // Error state
    error,
    
    // Actions
    refreshData,
    updateRemainingCashAmount,
    updateAllCustomerPayments,
    
    // Local state management
    localPaymentUpdates,
    updateLocalPayment,
    clearLocalUpdates,
    hasLocalChanges,

    // Payment entry management (from usePaymentEntries)
    addPaymentEntry: paymentHook.addPaymentEntry,
    updatePaymentEntry: paymentHook.updatePaymentEntry,
    deletePaymentEntry: paymentHook.deletePaymentEntry,
    savePaymentChanges: paymentHook.saveAllChanges,
    localPaymentEntries: paymentHook.localPaymentEntries,
    savingPayments: paymentHook.saving,
    hasPaymentChanges: paymentHook.hasChanges,

    // Auto-save
    autoSaveStatus,

    // Void order management
    handleToggleVoid,
  }
}
